import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCountries, getCountryCallingCode, type CountryCode } from "libphonenumber-js/max";
import { ChevronsUpDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";

const copy = {
  es: { country: "País del teléfono", search: "Buscar país o código…", empty: "Sin resultados", invalid: "Selecciona el país y escribe un número de teléfono válido." },
  en: { country: "Phone country", search: "Search country or code…", empty: "No results", invalid: "Select the country and enter a valid phone number." },
  fr: { country: "Pays du téléphone", search: "Rechercher un pays ou indicatif…", empty: "Aucun résultat", invalid: "Sélectionnez le pays et saisissez un numéro valide." },
  de: { country: "Telefonland", search: "Land oder Vorwahl suchen…", empty: "Keine Ergebnisse", invalid: "Wählen Sie das Land und geben Sie eine gültige Telefonnummer ein." },
  it: { country: "Paese del telefono", search: "Cerca paese o prefisso…", empty: "Nessun risultato", invalid: "Seleziona il paese e inserisci un numero valido." },
  pt: { country: "País do telefone", search: "Buscar país ou código…", empty: "Sem resultados", invalid: "Selecione o país e digite um número válido." },
};

const countryFlag = (country: CountryCode) => String.fromCodePoint(...[...country].map(char => 127397 + char.charCodeAt(0)));

export function BookingPhoneInput({ value, country, onChange, onCountryChange, invalid, id }: {
  value: string; country: CountryCode; onChange: (value: string) => void;
  onCountryChange: (country: CountryCode) => void; invalid: boolean; id?: string;
}) {
  const { i18n } = useTranslation();
  const lang = i18n.language.split("-")[0] as keyof typeof copy;
  const text = copy[lang] ?? copy.es;
  const [open, setOpen] = useState(false);
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const countries = useMemo(() => {
    const names = new Intl.DisplayNames([i18n.language || "es"], { type: "region" });
    return getCountries().map(id => ({ id, name: names.of(id) ?? id, code: getCountryCallingCode(id) }))
      .sort((a, b) => a.name.localeCompare(b.name, i18n.language));
  }, [i18n.language]);
  const selected = countries.find(c => c.id === country);
  return <>
    <div className="flex min-w-0 gap-2">
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" aria-expanded={open} aria-label={text.country}
            title={selected?.name} className="h-11 w-28 shrink-0 bg-background px-2 text-xs text-foreground">
            <span aria-hidden="true">{countryFlag(country)}</span><span>+{getCountryCallingCode(country)}</span><ChevronsUpDown />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="z-[100] w-72 max-w-[calc(100vw-2rem)] p-0">
          <Command>
            <CommandInput placeholder={text.search} />
            <CommandList><CommandEmpty>{text.empty}</CommandEmpty>
              {countries.map(c => <CommandItem key={c.id} value={`${c.name} ${c.id} +${c.code}`}
                className="min-h-11" onSelect={() => { onCountryChange(c.id); setOpen(false); }}>
                <span aria-hidden="true">{countryFlag(c.id)}</span><span className="min-w-0 flex-1">{c.name} (+{c.code})</span>
                {c.id === country && <Check />}
              </CommandItem>)}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Input id={inputId} type="tel" autoComplete="tel-national" className="h-11 min-w-0 flex-1 bg-background text-foreground"
        value={value} onChange={e => onChange(e.target.value)} aria-invalid={invalid}
        aria-describedby={invalid ? errorId : undefined} />
    </div>
    {invalid && <p id={errorId} role="alert" className="text-xs text-destructive">{text.invalid}</p>}
  </>;
}